import React from "react";
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Box,
  Skeleton,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LockIcon from "@mui/icons-material/Lock";
import useMetadata from "app/hooks/useMetadata";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { red } from "@mui/material/colors";

const VideoItem = ({
  video,
  index,
  sectionIndex,
  loadedVideos,
  setLoadedVideos,
  handlePlayVideo,
  getVideoThumbnail,
  getEmbedUrl,
  showLockedState = false,
}) => {
  const { metadata } = useMetadata();
  const embedUrl = getEmbedUrl(video.videoLink, video.provider);
  const isLoaded = loadedVideos[`${sectionIndex}-${index}`] || false;
  const thumbnailUrl = getVideoThumbnail(video);

  const handleVideoLoad = () => {
    setLoadedVideos((prev) => ({
      ...prev,
      [`${sectionIndex}-${index}`]: true,
    }));
  };

  const programmingLanguage = metadata?.programmingLanguages?.find(
    (lang) => lang.key === video.programmingLanguage
  );

  return (
    <ListItem
      sx={{
        p: (theme) => theme.spacing(1, 1),
        cursor:
          showLockedState && !video.previewAvailable ? "default" : "pointer",
        opacity: showLockedState && !video.previewAvailable ? 0.6 : 1,
        "&:hover": {
          backgroundColor: "#F5F5F5",
          borderRadius: 2,
        },
      }}
      onClick={() =>
        (!showLockedState || video.previewAvailable) && handlePlayVideo(video)
      }
    >
      <ListItemAvatar
        sx={{ mr: 2, width: 120, overflow: "hidden", borderRadius: 2 }}
      >
        {embedUrl ? (
          <Box sx={{ position: "relative", width: 120, height: 84 }}>
            {!isLoaded && (
              <Skeleton
                variant="rectangular"
                width={120}
                height={84}
                sx={{ borderRadius: "8px" }}
              />
            )}
            {video.provider === "YOUTUBE" || video.provider === "VIMEO" ? (
              <>
                <img
                  width="120"
                  height="84"
                  alt={video.videoTitle || "Video thumbnail"}
                  src={thumbnailUrl || ""}
                  style={{
                    verticalAlign: "middle",
                    display: isLoaded ? "block" : "none",
                  }}
                  onLoad={handleVideoLoad}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "";
                    e.target.style.display = "none";
                    handleVideoLoad();
                  }}
                />
                {isLoaded && !thumbnailUrl && (
                  <Typography
                    variant="h5"
                    sx={{
                      width: 120,
                      height: 84,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#e0e0e0",
                      color: "#757575",
                    }}
                  >
                    <YouTubeIcon fontSize="large" sx={{ color: red[500] }} />
                  </Typography>
                )}
              </>
            ) : (
              <Typography color="error">No thumbnail</Typography>
            )}
          </Box>
        ) : (
          <Typography color="error">No thumbnail</Typography>
        )}
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="h5" mb={0.25}>
            {video.videoTitle || "Untitled Video"}
          </Typography>
        }
        secondary={
          <>
            {video.videoDescription && (
              <Typography variant="body1" sx={{ color: "#8595A6", mb: 1 }}>
                {video.videoDescription}
              </Typography>
            )}
            {showLockedState && !video.previewAvailable ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip
                  title="Content Locked – Upgrade to Watch!"
                  arrow
                  placement="top"
                >
                  <Chip
                    icon={<LockIcon />}
                    label="Locked"
                    size="small"
                    variant="outlined"
                    color="default"
                  />
                </Tooltip>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="Play" arrow placement="top">
                  <IconButton
                    color="secondary"
                    variant="outlined"
                    aria-label="play"
                    onClick={() => handlePlayVideo(video)}
                    sx={{
                      "&:hover": {
                        opacity: 1,
                        borderRadius: 2,
                      },
                    }}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </Tooltip>
                {programmingLanguage?.value && (
                  <Chip
                    label={programmingLanguage.value}
                    size="small"
                    color="default"
                    sx={{
                      maxWidth: 150,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  />
                )}
              </Box>
            )}
          </>
        }
      />
    </ListItem>
  );
};

export default VideoItem;
